/**
 * A collator that never throws on the locale tag (MYAC-1002 e2e finding).
 *
 * `vue-i18n`'s `locale` can carry the RAW browser tag (the i18n plugin seeds
 * it from `navigator.languages`), and some environments report tags that are
 * not valid BCP-47 — observed live: headless Chromium on a POSIX-locale host
 * reporting `en-US@posix`. Passing that to `String.prototype.localeCompare`
 * throws `RangeError: Invalid language tag`, and a throw inside a computed's
 * `.sort()` comparator takes the whole render (and the table filters) down
 * with it. This helper falls back to the runtime's default locale instead.
 */
const cache = new Map<string, Intl.Collator>()

/**
 * `options` reaches the collator so a caller can ask for NUMERIC ordering —
 * clause codes need it: plain lexical comparison puts `4.10` before `4.2`,
 * which reads as broken to anyone scanning a referential. Cached per
 * locale+options, and the locale fallback above still applies.
 */
export function safeCollator(locale?: string, options?: Intl.CollatorOptions): Intl.Collator {
  const key = `${locale ?? ''}|${options ? JSON.stringify(options) : ''}`
  let collator = cache.get(key)
  if (!collator) {
    try {
      collator = new Intl.Collator(locale, options)
    } catch {
      collator = new Intl.Collator(undefined, options)
    }
    cache.set(key, collator)
  }
  return collator
}
